import * as React from 'react';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {SchedularPanelFormData} from 'src/types/analytics-genesis';
import {SchedularPanel} from 'src/components/analytics-genesis/schedular-panel/schedular-panel';
import {
  useByodPlacementSchedule,
  useByodWidgetSchedule,
  type UseByodScheduleResult,
} from 'src/components/analytics-genesis/byod/api';
import {
  byodScheduleToFormData,
  toByodScheduleCreateRequest,
  toByodScheduleUpdateRequest,
} from 'src/components/analytics-genesis/byod/api/schedule/schedule-form-mapping';

// Stable empty-path reference. The shared SchedularPanel derives its form
// defaults from `path` (via a memoized getInitialFormData); a fresh array
// identity each render recreates that memo and, in edit mode, re-runs the
// panel's form-reset effect every render — wiping in-progress edits and
// thrashing into "Maximum update depth exceeded". Passing a stable `path` (this
// constant for the empty case, useMemo for the breadcrumb) keeps BYOD in line
// with the pattern the panel's other consumers already follow.
const EMPTY_PATH: string[] = [];

type ByodSchedulePanelViewProps = {
  isOpen: boolean;
  onClose: () => void;
  reportName: string;
  product: string;
  dashboard: string;
  path?: string[];
  resource: UseByodScheduleResult;
};

// Presentational controller shared by the widget and placement flows. Reads the
// active schedule off the resource hook to pick create vs edit mode, maps the
// SchedularPanel's form data to/from the BYOD contract, and closes on success.
function ByodSchedulePanelView({
  isOpen,
  onClose,
  reportName,
  product,
  dashboard,
  path,
  resource,
}: ByodSchedulePanelViewProps) {
  const {
    schedule,
    isLoading,
    isLoadError,
    isDeleting,
    createSchedule,
    updateSchedule,
    deleteSchedule,
  } = resource;
  const isEdit = schedule != null;

  // A failed GET is not "no schedule exists" — presenting the create form on an
  // errored load would risk a duplicate POST (the placement endpoint has no 409
  // backstop). The hook already surfaced the error toast; close the panel so
  // the user can reopen to retry against a fresh GET.
  React.useEffect(() => {
    if (isLoadError) {
      onClose();
    }
  }, [isLoadError, onClose]);

  const initialData = React.useMemo<
    Partial<SchedularPanelFormData> | undefined
  >(
    () => (schedule ? byodScheduleToFormData(schedule) : undefined),
    [schedule],
  );

  const handleSave = React.useCallback(
    async (data: SchedularPanelFormData): Promise<void> => {
      // A throw here is caught by SchedularPanel.onSubmit (which keeps the panel
      // open for retry); onClose is reached only when the mutation resolves.
      if (isEdit) {
        await updateSchedule(toByodScheduleUpdateRequest(data));
      } else {
        await createSchedule(toByodScheduleCreateRequest(data));
      }
      onClose();
    },
    [isEdit, updateSchedule, createSchedule, onClose],
  );

  const handleDelete = React.useCallback(async (): Promise<void> => {
    try {
      await deleteSchedule();
    } catch {
      // Failure toast is surfaced by the hook; keep the panel open to retry.
      return;
    }
    onClose();
  }, [deleteSchedule, onClose]);

  return (
    <SchedularPanel
      isOpen={isOpen}
      onClose={onClose}
      mode={isEdit ? 'edit' : 'create'}
      isLoading={isLoading}
      reportName={reportName}
      product={product}
      dashboard={dashboard}
      path={path ?? EMPTY_PATH}
      initialData={initialData}
      onSave={handleSave}
      onCancel={onClose}
      onDelete={isEdit ? handleDelete : undefined}
      isDeleting={isDeleting}
      // BYOD's backend caps day_of_month at 28 with no "last day" concept, so
      // hide the option rather than silently persisting it as the 28th.
      hideLastDayOfMonth
    />
  );
}

export type ByodWidgetSchedulePanelProps = {
  /** Catalog widget key (Reports Library standalone report). */
  widgetKey: string;
  /** Report display name — prefilled as the schedule's report name. */
  reportName: string;
  isOpen: boolean;
  onClose: () => void;
};

// Reports Library flow: schedules a standalone table widget by widget_key.
export function ByodWidgetSchedulePanel({
  widgetKey,
  reportName,
  isOpen,
  onClose,
}: ByodWidgetSchedulePanelProps) {
  const {t} = useLabelTranslation();
  // `enabled: false` — the ByodTable entry point is create-only: the panel
  // always opens in create mode and never fetches (or edits) an existing
  // schedule. `schedule` therefore always resolves to null, so each save POSTs
  // a new schedule (no 409 handling). Managing existing schedules
  // (view/pause/edit/delete) is done from Settings → Reports, not here.
  const resource = useByodWidgetSchedule(widgetKey, {enabled: false});
  return (
    <ByodSchedulePanelView
      isOpen={isOpen}
      onClose={onClose}
      reportName={reportName}
      product={t('Reports', 'Reports')}
      dashboard={reportName}
      resource={resource}
    />
  );
}

export type ByodPlacementSchedulePanelProps = {
  /** Placement id (AnalyticsCustomDashboardWidget.id) of the dashboard tile. */
  placementId: string;
  /** Tile/report display name. */
  reportName: string;
  /** Parent dashboard name for the breadcrumb; falls back to the report name. */
  dashboardName?: string;
  isOpen: boolean;
  onClose: () => void;
};

// Dashboard flow: schedules a table tile by its placement id.
export function ByodPlacementSchedulePanel({
  placementId,
  reportName,
  dashboardName,
  isOpen,
  onClose,
}: ByodPlacementSchedulePanelProps) {
  const {t} = useLabelTranslation();
  // `enabled: false` — the panel opens immediately without waiting on (or
  // depending on) the existing-schedule GET. `schedule` always resolves to
  // null, so the panel always opens in create mode; editing/deleting an
  // already-scheduled placement isn't available from this entry point.
  const resource = useByodPlacementSchedule(placementId, {enabled: false});
  // Memoize so the panel receives a stable `path` identity (see EMPTY_PATH).
  const path = React.useMemo(
    () => (dashboardName ? [dashboardName] : EMPTY_PATH),
    [dashboardName, reportName],
  );
  return (
    <ByodSchedulePanelView
      isOpen={isOpen}
      onClose={onClose}
      reportName={reportName}
      product={t('Dashboards', 'Dashboards')}
      dashboard={dashboardName ?? reportName}
      path={path}
      resource={resource}
    />
  );
}
